import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  BookOpen,
  Boxes,
  ChevronRight,
  Clapperboard,
  Download,
  Gamepad2,
  Pause,
  Play,
  ShieldCheck,
  SkipBack,
  SkipForward,
  Sparkles,
  Shield,
  Users,
  Volume2,
} from 'lucide-react';
import { Link, NavLink, Outlet, Route, Routes } from 'react-router-dom';
import StudioHome from '@/pages/StudioHome';
import DahuangSite from '@/pages/DahuangSite';
import {
  boardingContent,
  communityEntries,
  featureCards,
  galleryEntries,
  guideEntries,
  heroContent,
  legalEntries,
  modEditorShowcaseEntries,
  modEditorShowcaseFootnote,
  modEditorShowcaseLead,
  officialDevLogEntries,
  platformEntries,
  siteLinks,
  soundtrackEntries,
  trailerContent,
  updateEntries,
} from '@/lib/siteConfig';

function openExternal(href?: string) {
  if (!href) return;
  window.open(href, '_blank', 'noopener,noreferrer');
}

// 房车官网从根路径迁移到 /rebirth-hoarder 子路径（根路径改为工作室介绍页）。
// 注意：/activation、/mod-editor/、/privacy-policy、/user-agreement 仍保持根路径不变，
// 因为已上线的房车客户端硬编码了这些地址（如 Steam 登录回调 `${origin}/activation`）。
const GAME_BASE = '/rebirth-hoarder';

function SectionHeader({ eyebrow, title, description }: { eyebrow: string; title: string; description: string }) {
  return (
    <div className="section-header">
      <span className="section-header__eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      <p>{description}</p>
    </div>
  );
}

function SoundtrackPlayer() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const currentTrack = soundtrackEntries[currentTrackIndex];

  useEffect(() => {
    setProgress(0);
  }, [currentTrack.file]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!isPlaying) {
      audio.pause();
      return;
    }

    const playPromise = audio.play();
    if (playPromise) {
      playPromise.catch(() => {
        setIsPlaying(false);
      });
    }
  }, [currentTrack.file, isPlaying]);

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) {
      setProgress(0);
      return;
    }
    setProgress((audio.currentTime / audio.duration) * 100);
  };

  const selectRelativeTrack = (delta: number) => {
    setCurrentTrackIndex((prev) => {
      const total = soundtrackEntries.length;
      return (prev + delta + total) % total;
    });
  };

  return (
    <div className="soundtrack-player">
      <audio
        ref={audioRef}
        src={currentTrack.file}
        preload="metadata"
        onEnded={() => selectRelativeTrack(1)}
        onLoadedMetadata={handleTimeUpdate}
        onTimeUpdate={handleTimeUpdate}
      />
      <img className="soundtrack-player__cover" src={currentTrack.cover} alt="" />
      <div className="soundtrack-player__body">
        <span className="soundtrack-player__label">
          <Volume2 size={14} />
          车载音乐 {String(currentTrackIndex + 1).padStart(2, '0')} / {String(soundtrackEntries.length).padStart(2, '0')}
        </span>
        <strong title={currentTrack.title}>{currentTrack.title}</strong>
        <span title={currentTrack.note}>{currentTrack.note}</span>
        <div className="soundtrack-player__progress" aria-hidden="true">
          <span style={{ width: `${progress}%` }} />
        </div>
      </div>
      <div className="soundtrack-player__transport">
        <button type="button" aria-label="上一首" onClick={() => selectRelativeTrack(-1)}>
          <SkipBack size={16} />
        </button>
        <button
          type="button"
          aria-label={isPlaying ? '暂停播放' : '开始播放'}
          className="soundtrack-player__toggle"
          onClick={() => setIsPlaying((prev) => !prev)}
        >
          {isPlaying ? <Pause size={16} /> : <Play size={16} />}
        </button>
        <button type="button" aria-label="下一首" onClick={() => selectRelativeTrack(1)}>
          <SkipForward size={16} />
        </button>
      </div>
    </div>
  );
}

function SiteNavigation() {
  return (
    <header className="site-nav">
      <Link className="site-brand" to={GAME_BASE}>
        <span className="site-brand__eyebrow">OFFICIAL RELEASE DESK</span>
        <span className="site-brand__title">末世：我有一辆房车</span>
      </Link>

      <nav className="site-nav__links">
        <NavLink to={GAME_BASE} end>
          首页
        </NavLink>
        <NavLink to={`${GAME_BASE}/guides`}>攻略</NavLink>
        <NavLink to={`${GAME_BASE}/updates`}>更新</NavLink>
        <NavLink to="/">光年切片工作室</NavLink>
      </nav>

      <div className="site-nav__actions">
        <SoundtrackPlayer />
        <button type="button" className="site-nav__link--button" onClick={() => openExternal(siteLinks.tapTap)}>
          安卓下载
        </button>
        <a href={siteLinks.modEditor} className="site-nav__cta">
          进入 MOD 编辑器
        </a>
      </div>
    </header>
  );
}

function HomePage() {
  const [activeShowcaseIndex, setActiveShowcaseIndex] = useState(0);
  const activeShowcase = modEditorShowcaseEntries[activeShowcaseIndex] ?? modEditorShowcaseEntries[0];

  useEffect(() => {
    if (modEditorShowcaseEntries.length <= 1) return undefined;

    const timer = window.setInterval(() => {
      setActiveShowcaseIndex((current) => (current + 1) % modEditorShowcaseEntries.length);
    }, 4600);

    return () => window.clearInterval(timer);
  }, []);

  return (
    <main className="site-page">
      <section className="hero-panel">
        <div className="hero-panel__backdrop" style={{ backgroundImage: `url(${heroContent.background})` }} />
        <div className="hero-panel__grid" />
        <div className="hero-panel__content">
          <div className="hero-copy">
            <span className="hero-copy__eyebrow">{heroContent.eyebrow}</span>
            <h1>{heroContent.title}</h1>
            <p className="hero-copy__subtitle">{heroContent.subtitle}</p>
            <p className="hero-copy__description">{heroContent.description}</p>
            <div className="hero-copy__highlight">
              <Sparkles size={16} />
              <span>{heroContent.highlight}</span>
            </div>
            <div className="hero-actions">
              <button type="button" className="hero-button hero-button--primary" onClick={() => openExternal(siteLinks.tapTap)}>
                <Download size={18} />
                安卓下载
              </button>
              <Link className="hero-button hero-button--secondary" to={siteLinks.activation}>
                <ShieldCheck size={18} />
                Steam 免广领取
              </Link>
              <a className="hero-button hero-button--secondary" href={siteLinks.modEditor}>
                <Boxes size={18} />
                MOD 编辑器
              </a>
              <button type="button" className="hero-button hero-button--ghost" onClick={() => openExternal(siteLinks.forum)}>
                <BookOpen size={18} />
                论坛攻略
              </button>
            </div>
            <div className="hero-badges">
              {heroContent.badges.map((badge) => (
                <span key={badge}>{badge}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="content-section">
        <SectionHeader eyebrow="PLATFORMS" title="下载与预约" description="安卓正式入口已经开放，iOS 与 Steam 的最新动态也会在这里第一时间公布。" />
        <div className="platform-grid">
          {platformEntries.map((entry) => (
            <article key={entry.id} className={clsx('platform-card', entry.status === 'available' && 'platform-card--active')}>
              <div className="platform-card__topline">
                <span className="platform-card__kicker">{entry.kicker}</span>
                <span className={clsx('status-pill', `status-pill--${entry.status}`)}>{entry.note}</span>
              </div>
              <h3>{entry.title}</h3>
              <p>{entry.description}</p>
              <button
                type="button"
                className={clsx('platform-card__cta', !entry.href && 'platform-card__cta--disabled')}
                onClick={() => openExternal(entry.href)}
                disabled={!entry.href}
              >
                {entry.cta}
                <ChevronRight size={16} />
              </button>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section">
        <SectionHeader eyebrow="OFFICIAL TRAILER" title={trailerContent.title} description={trailerContent.description} />
        <div className="trailer-layout">
          <article className="trailer-stage">
            <div className="trailer-stage__frame">
              <video className="trailer-stage__video" controls playsInline preload="none" poster={trailerContent.poster}>
                <source src={trailerContent.video} type="video/mp4" />
              </video>
            </div>
          </article>

          <aside className="trailer-aside">
            <img className="trailer-aside__image" src={trailerContent.asideImage} alt="" />
            <div className="trailer-aside__copy">
              <span className="trailer-aside__eyebrow">
                <Clapperboard size={14} />
                官方视听片段
              </span>
              <h3>{trailerContent.asideTitle}</h3>
              <p>{trailerContent.asideDescription}</p>
              <div className="trailer-aside__actions">
                <button type="button" className="hero-button hero-button--primary" onClick={() => openExternal(siteLinks.tapTap)}>
                  <Download size={18} />
                  看完就上车
                </button>
                <Link to={`${GAME_BASE}/updates`} className="hero-button hero-button--ghost">
                  <Sparkles size={18} />
                  查看版本亮点
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="content-section">
        <SectionHeader eyebrow="WORKBENCH" title="PC Web MOD 编辑器" description="给玩家和作者准备的大屏创作工作台。设计 NPC、编辑微信聊天、配置剧情弹窗、新闻、朋友圈与物品，一套工程即可编译导出。" />
        <div className="editor-showcase">
          <div className="editor-showcase__stage">
            <div className="editor-showcase__stage-topline">
              <span className="editor-spotlight__badge">滚动展示 / {activeShowcase.eyebrow}</span>
              <span className="editor-showcase__counter">
                {String(activeShowcaseIndex + 1).padStart(2, '0')} / {String(modEditorShowcaseEntries.length).padStart(2, '0')}
              </span>
            </div>
            <div className="editor-showcase__frame">
              {modEditorShowcaseEntries.map((entry, index) => (
                <div
                  key={entry.id}
                  className={clsx('editor-showcase__slide', index === activeShowcaseIndex && 'editor-showcase__slide--active')}
                >
                  <div className="editor-showcase__slide-backdrop" style={{ backgroundImage: `url(${entry.image})` }} />
                  <img
                    src={entry.image}
                    alt={entry.title}
                    className="editor-showcase__image"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
            <div className="editor-showcase__thumbnail-row">
              {modEditorShowcaseEntries.map((entry, index) => (
                <button
                  key={entry.id}
                  type="button"
                  className={clsx('editor-showcase__thumbnail', index === activeShowcaseIndex && 'editor-showcase__thumbnail--active')}
                  onClick={() => setActiveShowcaseIndex(index)}
                >
                  <img src={entry.image} alt="" loading="lazy" />
                  <span>{entry.title}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="editor-showcase__panel">
            <span className="editor-spotlight__badge">官方创作入口</span>
            <h3>{activeShowcase.title}</h3>
            <p className="editor-showcase__lead">{modEditorShowcaseLead}</p>
            <p>{activeShowcase.description}</p>
            <div className="editor-showcase__chips">
              {activeShowcase.chips.map((chip) => (
                <span key={chip}>{chip}</span>
              ))}
            </div>
            <div className="editor-showcase__metrics">
              {activeShowcase.highlights.map((highlight) => (
                <div key={highlight} className="editor-showcase__metric">
                  <strong>{highlight}</strong>
                </div>
              ))}
              <div className="editor-showcase__metric editor-showcase__metric--accent">
                <strong>手机游戏端已同步上线内置版创意工坊</strong>
                <span>{modEditorShowcaseFootnote}</span>
              </div>
            </div>
            <div className="editor-showcase__actions">
              <a href={siteLinks.modEditor} className="hero-button hero-button--primary">
                <Boxes size={18} />
                打开 MOD 编辑器
              </a>
              <button type="button" className="hero-button hero-button--secondary" onClick={() => openExternal(siteLinks.tapTap)}>
                <Download size={18} />
                安卓端体验内置创意工坊
              </button>
              <button type="button" className="hero-button hero-button--ghost" onClick={() => openExternal(siteLinks.forum)}>
                <BookOpen size={18} />
                先看攻略讨论
              </button>
            </div>
            <div className="editor-showcase__footnote">
              <span>PC Web 图形工作台</span>
              <span>导出 `.rvmod` 兼容创意工坊</span>
              <span>手机端内置版同步可用</span>
            </div>
          </div>
        </div>
      </section>

      <section className="content-section">
        <SectionHeader eyebrow="CORE GAMEPLAY" title="核心玩法" description="从房车经营到废墟探索，再到微信叙事和队员养成，多个系统共同构成末世生存的主循环。" />
        <div className="feature-grid">
          {featureCards.map((card) => (
            <article key={card.title} className="feature-card">
              {card.image ? <img src={card.image} alt="" className="feature-card__image" loading="lazy" /> : null}
              <div className="feature-card__overlay" />
              <div className="feature-card__content">
                <h3>{card.title}</h3>
                <p>{card.description}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section">
        <SectionHeader eyebrow="GALLERY" title="截图与角色" description="从角色立绘到玩法截图，官网首页直接使用游戏内现有资产，还原这个世界的气质和内容密度。" />
        <div className="gallery-grid">
          {galleryEntries.map((entry) => (
            <article key={entry.title} className={clsx('gallery-card', `gallery-card--${entry.variant ?? 'standard'}`)}>
              {entry.image ? <img src={entry.image} alt="" className="gallery-card__image" loading="lazy" /> : null}
              <div className="gallery-card__copy">
                <h3>{entry.title}</h3>
                <p>{entry.description}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section">
        <div className="boarding-banner">
          <div className="boarding-banner__media">
            <img src={boardingContent.image} alt="" />
          </div>
          <div className="boarding-banner__copy">
            <span className="section-header__eyebrow">BOARD NOW</span>
            <h2>{boardingContent.title}</h2>
            <p className="boarding-banner__subtitle">{boardingContent.subtitle}</p>
            <p>{boardingContent.description}</p>
            <div className="hero-actions">
              <button type="button" className="hero-button hero-button--primary" onClick={() => openExternal(siteLinks.tapTap)}>
                <Download size={18} />
                前往安卓下载
              </button>
              <a href={siteLinks.modEditor} className="hero-button hero-button--secondary">
                <Boxes size={18} />
                去试 MOD 编辑器
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="content-section content-section--split">
        <div className="split-panel">
          <SectionHeader eyebrow="GUIDES" title="论坛攻略与精选资料" description="站内负责品牌化整理，讨论与持续更新则统一引导到 TapTap 论坛。" />
          <div className="guide-stack">
            {guideEntries.map((entry) => (
              <article key={entry.title} className={clsx('guide-card', entry.highlight && 'guide-card--highlight')}>
                <div className="guide-card__badge">{entry.badge}</div>
                <h3>{entry.title}</h3>
                <p>{entry.description}</p>
                <button type="button" className="guide-card__cta" onClick={() => openExternal(entry.href)}>
                  {entry.cta}
                  <ArrowRight size={16} />
                </button>
              </article>
            ))}
          </div>
        </div>

        <div className="split-panel">
          <SectionHeader eyebrow="UPDATES" title="版本更新亮点" description="用图文专题快速回顾近几个版本的方向变化和系统扩展。" />
          <div className="update-teaser-stack">
            {updateEntries.slice(0, 4).map((entry) => (
              <article key={entry.version} className="update-teaser">
                {entry.image ? <img src={entry.image} alt="" className="update-teaser__image" loading="lazy" /> : null}
                <div className="update-teaser__copy">
                  <span>{entry.version}</span>
                  <h3>{entry.title}</h3>
                  <p>{entry.summary}</p>
                </div>
              </article>
            ))}
            <Link to={`${GAME_BASE}/updates`} className="split-panel__link">
              查看完整更新日志
              <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section className="content-section">
        <SectionHeader eyebrow="COMMUNITY" title="社区与法律" description="加入官方群、提交反馈、查看协议说明，让内容创作和玩家交流在同一入口完成。" />
        <div className="info-grid">
          <div className="info-grid__column">
            {communityEntries.map((entry) => (
              <article key={entry.title} className="info-card">
                <div className="info-card__icon">
                  <Users size={18} />
                </div>
                <div className="info-card__content">
                  <h3>{entry.title}</h3>
                  <p>{entry.description}</p>
                  <strong>{entry.value}</strong>
                </div>
                {entry.href ? (
                  <button type="button" className="info-card__action" onClick={() => openExternal(entry.href)}>
                    前往
                  </button>
                ) : null}
              </article>
            ))}
          </div>
          <div className="info-grid__column">
            {legalEntries.map((entry) => (
              <article key={entry.title} className="info-card">
                <div className="info-card__icon">
                  <ShieldCheck size={18} />
                </div>
                <div className="info-card__content">
                  <h3>{entry.title}</h3>
                  <p>{entry.description}</p>
                </div>
                <a href={entry.href} className="info-card__action">
                  查看
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

function GuidesPage() {
  return (
    <main className="site-page site-page--subpage">
      <section className="subpage-hero">
        <div className="subpage-hero__copy">
          <span className="section-header__eyebrow">GUIDES</span>
          <h1>攻略中心</h1>
          <p>论坛是主阵地，站内则把新手资料与重点专题收束成更清晰的入口。想看实时讨论、官方答疑和玩家流派分享，直接进入 TapTap 论坛。</p>
          <div className="hero-actions">
            <button type="button" className="hero-button hero-button--primary" onClick={() => openExternal(siteLinks.forum)}>
              <BookOpen size={18} />
              前往 TapTap 论坛
            </button>
            <button type="button" className="hero-button hero-button--ghost" onClick={() => openExternal(siteLinks.tapTap)}>
              <Gamepad2 size={18} />
              查看游戏主页
            </button>
          </div>
        </div>
      </section>

      <section className="content-section">
        <SectionHeader eyebrow="SELECTED" title="精选资料" description="先用这几份资料快速建立基础理解，再去论坛追版本讨论和玩家心得。" />
        <div className="guide-stack guide-stack--wide">
          {guideEntries.map((entry) => (
            <article key={entry.title} className={clsx('guide-card', entry.highlight && 'guide-card--highlight')}>
              <div className="guide-card__badge">{entry.badge}</div>
              <h3>{entry.title}</h3>
              <p>{entry.description}</p>
              <button type="button" className="guide-card__cta" onClick={() => openExternal(entry.href)}>
                {entry.cta}
                <ArrowRight size={16} />
              </button>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

function UpdatesPage() {
  return (
    <main className="site-page site-page--subpage">
      <section className="subpage-hero">
        <div className="subpage-hero__copy">
          <span className="section-header__eyebrow">PATCH NOTES</span>
          <h1>更新日志</h1>
          <p>从 v3.0 到 v3.8.5 的版本专题，再到 17 篇 TapTap 官方开发日志，这里把重要更新入口统一收在一起，方便集中回看。</p>
        </div>
      </section>

      <section className="content-section">
        <div className="timeline-list">
          {updateEntries.map((entry) => (
            <article key={entry.version} className="timeline-card">
              <div className="timeline-card__meta">
                <span className="timeline-card__version">{entry.version}</span>
                <div className="timeline-card__line" />
              </div>
              <div className="timeline-card__body">
                {entry.image ? <img src={entry.image} alt="" className="timeline-card__image" loading="lazy" /> : null}
                <div className="timeline-card__content">
                  <h2>{entry.title}</h2>
                  <p>{entry.summary}</p>
                  {entry.spotlight ? (
                    <div className="timeline-card__spotlight">
                      <img src={entry.spotlight} alt="" />
                      <span>版本专题画面</span>
                    </div>
                  ) : null}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section">
        <SectionHeader
          eyebrow="OFFICIAL DEV LOGS"
          title="官方开发日志归档"
          description="以下 17 篇均整理自 TapTap 官方开发日志。站内保留时间轴和摘要，点击即可前往原帖查看完整内容与评论。"
        />
        <div className="devlog-archive">
          <div className="devlog-archive__summary">
            <div>
              <span className="section-header__eyebrow">17 篇原帖入口</span>
              <h2>TapTap 官方日志完整归档</h2>
              <p>想补看开发历程、版本节奏和每次大更新的原始说明，可以从这里逐篇进入，不用再在论坛里反复翻找。</p>
            </div>
            <button type="button" className="hero-button hero-button--ghost" onClick={() => openExternal(siteLinks.officialDevLogs)}>
              <BookOpen size={18} />
              打开 TapTap 官方合集
            </button>
          </div>

          <div className="devlog-grid">
            {officialDevLogEntries.map((entry) => (
              <article key={entry.href} className="devlog-card">
                <div className="devlog-card__topline">
                  <span className="timeline-card__version">LOG {String(entry.index).padStart(2, '0')}</span>
                  <span className="devlog-card__date">{entry.date}</span>
                </div>
                <h3>{entry.title}</h3>
                <p>{entry.summary}</p>
                <button type="button" className="guide-card__cta" onClick={() => openExternal(entry.href)}>
                  前往 TapTap 查看全文
                  <ArrowRight size={16} />
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

function ActivationPage() {
  const search = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const activationStatus = search.get('activation');
  const errorMessage = (search.get('message') || '').trim();
  const code = search.get('code') || '';
  const expiresAt = Number(search.get('expiresAt') || '0');
  const [manualProfile, setManualProfile] = useState('');
  const [manualSession, setManualSession] = useState<{
    state: string;
    steamId: string;
    personaName: string;
    verifyCode: string;
    expiresAt: number;
  } | null>(null);
  const [manualCode, setManualCode] = useState('');
  const [manualBusy, setManualBusy] = useState(false);
  const [manualMessage, setManualMessage] = useState('');
  const hasCode = code.trim().length > 0;
  const displayCode = manualCode || code;
  const hasDisplayCode = displayCode.trim().length > 0;
  const displayExpirySource = manualSession && manualCode ? manualSession.expiresAt : expiresAt;
  const displayExpiry = displayExpirySource > 0 ? new Date(displayExpirySource).toLocaleString('zh-CN') : '领取后长期可用，建议尽快绑定';
  const showError = activationStatus === 'error' && errorMessage.length > 0;
  const alreadyBound = errorMessage.includes('已完成手游免广绑定');
  const showManualFallback = !hasDisplayCode && !alreadyBound;
  const activationOwnershipNotice = 'Steam 免广会在后续 Tap 登录时自动校验 Steam 持有状态，如已退款或取消购买，手游免广会自动失效。';

  const copyCode = async () => {
    if (!hasDisplayCode || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(displayCode);
    } catch {
      // ignore clipboard failures
    }
  };

  const startManualCheck = async () => {
    const profile = manualProfile.trim();
    if (!profile) {
      setManualMessage('请先输入 Steam 个人主页链接、自定义链接或 17 位 SteamID。');
      return;
    }
    setManualBusy(true);
    setManualMessage('正在生成 Steam 昵称验证码...');
    try {
      const response = await fetch('/api/community/activation/steam/nickname/start', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ steamProfile: profile, returnUrl: window.location.href }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.detail || '验证码生成失败，请稍后重试。');
      }
      setManualSession(payload);
      setManualCode('');
      setManualMessage(`请把验证码 ${payload.verifyCode} 加到 Steam 昵称中，再点击校验领取。`);
    } catch (error) {
      setManualMessage(error instanceof Error ? error.message : '验证码生成失败，请稍后重试。');
    } finally {
      setManualBusy(false);
    }
  };

  const confirmManualCheck = async () => {
    if (!manualSession) return;
    setManualBusy(true);
    setManualMessage('正在校验 Steam 昵称与买断所有权...');
    try {
      const response = await fetch('/api/community/activation/steam/nickname/confirm', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ state: manualSession.state }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.detail || '校验失败，请确认昵称已保存并稍后重试。');
      }
      setManualCode(payload.code || '');
      setManualSession((current) => current ? { ...current, expiresAt: Number(payload.expiresAt || current.expiresAt) } : current);
      setManualMessage('校验通过，已生成手游免广激活码。');
    } catch (error) {
      setManualMessage(error instanceof Error ? error.message : '校验失败，请确认昵称已保存并稍后重试。');
    } finally {
      setManualBusy(false);
    }
  };

  return (
    <main className="site-page site-page--subpage">
      <section className="subpage-hero">
        <div className="subpage-hero__copy">
          <span className="section-header__eyebrow">STEAM BONUS</span>
          <h1>Steam 买断赠送手游免广</h1>
          <p>先用 Steam 账号验证 PC 买断所有权，验证通过后即可领取一次性手游免广激活码，再回到安卓端输入绑定。</p>
        </div>
      </section>

      <section className="content-section">
        <div className="activation-card">
          <div className="activation-card__header">
            <div>
              <span className="section-header__eyebrow">OWNERSHIP CHECK</span>
              <h2>官网领取页</h2>
            </div>
            <span className={clsx(
              'status-pill',
              activationStatus === 'ok'
                ? 'status-pill--available'
                : alreadyBound
                  ? 'status-pill--available'
                : activationStatus === 'error'
                  ? 'status-pill--error'
                  : 'status-pill--coming',
            )}>
              {activationStatus === 'ok' ? '已通过校验' : alreadyBound ? '已完成绑定' : activationStatus === 'error' ? '校验失败' : '等待验证'}
            </span>
          </div>

          <p className="activation-card__lead">
            说明：只有 Steam PC 买断所有权可以领取，家庭共享与临时授权当前不在首发范围内。
          </p>
          <p className="activation-card__notice">{activationOwnershipNotice}</p>

          {showError && !alreadyBound ? (
            <div className="activation-error-panel">
              <strong>本次 Steam 校验未完成</strong>
              <p>{errorMessage}</p>
            </div>
          ) : null}

          {alreadyBound ? (
            <div className="activation-code-panel">
              <div className="activation-code-panel__icon">
                <Shield size={22} />
              </div>
              <div className="activation-code-panel__body">
                <span>该 Steam 账号已完成绑定</span>
                <strong>无需重复领取</strong>
                <p>这个 Steam 账号已经完成手游免广绑定，当前不再重复签发新激活码。如需处理换绑或异常，请联系客服核查。</p>
              </div>
            </div>
          ) : null}

          {hasDisplayCode ? (
            <div className="activation-code-panel">
              <div className="activation-code-panel__icon">
                <Shield size={22} />
              </div>
              <div className="activation-code-panel__body">
                <span>你的手游免广激活码</span>
                <strong>{displayCode}</strong>
                <p>请在手机端主菜单头像入口的“福利与免广激活台”中输入绑定。当前码有效期至：{displayExpiry}</p>
                <p>{activationOwnershipNotice}</p>
              </div>
              <button type="button" className="hero-button hero-button--primary" onClick={copyCode}>
                复制激活码
              </button>
            </div>
          ) : !alreadyBound ? (
            <div className="activation-actions">
              <button
                type="button"
                className="hero-button hero-button--primary"
                onClick={() => openExternal(`${window.location.origin}/api/community/activation/steam/start?returnUrl=${encodeURIComponent(`${window.location.origin}/activation`)}`)}
              >
                使用 Steam 登录并领取
              </button>
              <button type="button" className="hero-button hero-button--ghost" onClick={() => openExternal(siteLinks.steam)}>
                先看 Steam 商店页
              </button>
            </div>
          ) : null}

          {showManualFallback ? (
            <div className="activation-manual-panel">
              <div>
                <span className="section-header__eyebrow">FALLBACK VERIFY</span>
                <h3>Steam 昵称验证码领取</h3>
              <p>如果登录回调受网络影响失败，可用 Steam 公开昵称临时包含验证码来证明账号归属，再由服务器校验 PC 买断所有权。{activationOwnershipNotice}</p>
              </div>
              <div className="activation-manual-panel__form">
                <input
                  type="text"
                  value={manualProfile}
                  onChange={(event) => setManualProfile(event.target.value)}
                  placeholder="Steam 个人主页链接 / 自定义链接 / 17 位 SteamID"
                />
                <button type="button" className="hero-button hero-button--secondary" onClick={startManualCheck} disabled={manualBusy}>
                  生成验证码
                </button>
              </div>
              {manualSession ? (
                <div className="activation-verify-code">
                  <span>请临时加入 Steam 昵称的验证码</span>
                  <strong>{manualSession.verifyCode}</strong>
                  <p>当前读取到的昵称：{manualSession.personaName}。修改 Steam 昵称后可能有短暂缓存，建议等待 10-30 秒再校验。</p>
                  <button type="button" className="hero-button hero-button--primary" onClick={confirmManualCheck} disabled={manualBusy}>
                    校验并领取激活码
                  </button>
                </div>
              ) : null}
              {manualMessage ? <p className="activation-manual-panel__message">{manualMessage}</p> : null}
            </div>
          ) : null}

          <div className="activation-steps">
            <article>
              <span>01</span>
              <div>
                <h3>登录 Steam</h3>
                <p>在官网完成 Steam OpenID 登录，并由服务器向 Steam 校验当前账号的 PC 买断所有权。</p>
              </div>
            </article>
            <article>
              <span>02</span>
              <div>
                <h3>领取一次性激活码</h3>
                <p>校验通过后，服务器会即时签发新的手游免广激活码。未使用旧码会自动失效，避免外泄复用。</p>
              </div>
            </article>
            <article>
              <span>03</span>
              <div>
                <h3>回到安卓端绑定</h3>
                <p>在游戏主菜单打开“福利与免广激活台”，输入激活码后即可获得服务器免广权益。</p>
              </div>
            </article>
          </div>
        </div>
      </section>
    </main>
  );
}

// 房车官网外壳（导航 + 页脚），子页面通过 <Outlet /> 渲染。
function GameLayout() {
  return (
    <div className="site-shell">
      <div className="site-shell__noise" />
      <SiteNavigation />
      <Outlet />
      <footer className="site-footer">
        <div className="site-footer__left">
          <span>末世：我有一辆房车</span>
          <span>官方发布台 / MOD 创作入口 / 社区导航</span>
        </div>
        <div className="site-footer__right">
          <Link to={siteLinks.activation}>Steam 免广领取</Link>
          <a href={siteLinks.modEditor}>MOD 编辑器</a>
          <a href={siteLinks.privacy}>隐私政策</a>
          <a href={siteLinks.agreement}>用户协议</a>
        </div>
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      {/* 光年切片工作室介绍页（根路径）与《回到大荒》预告站 */}
      <Route path="/" element={<StudioHome />} />
      <Route path="/dahuang" element={<DahuangSite />} />

      {/* 《末世：我有一辆房车》官网迁移至 /rebirth-hoarder 子路径 */}
      <Route path="/rebirth-hoarder" element={<GameLayout />}>
        <Route index element={<HomePage />} />
        <Route path="guides" element={<GuidesPage />} />
        <Route path="updates" element={<UpdatesPage />} />
      </Route>

      {/* 免广领取页保持根路径 /activation 不变（已上线客户端硬编码依赖） */}
      <Route path="/activation" element={<GameLayout />}>
        <Route index element={<ActivationPage />} />
      </Route>
    </Routes>
  );
}
